import { useState } from "react";
import PropTypes from "prop-types";
import AppUtils from "@src/utils/appUtils";
import OperatorHooks from "@src/context/hooks/operatorHooks";
import { useOperatorContext } from "@src/context/operatorContext";
import { trackActionActivity } from "@src/utils/userActivityTracker";
import { OnboardingWelcome } from "./Step0";
import { OnboardingStep4 } from "./Step4";
import { OnboardingCommitmentConfirmation } from "./CommitmentConfirmation";
import {
  OnboardingShippingStep1,
  OnboardingAviationStep1,
  OnboardingAviationStep2,
  OnboardingAviationStep3,
} from "./IndustryStep";

export const OnboardingContainer = () => {
  const { profile, setProfile, setOnboardingOpen } = useOperatorContext();
  const [step, setStep] = useState(0);
  const [previousStep, setPreviousStep] = useState(0);
  const [selectedCommitment, setSelectedCommitment] = useState(null);

  const isShipping = profile.customer.industry === "shipping";

  const steps = [
    OnboardingWelcome,
    ...(isShipping
      ? [OnboardingShippingStep1]
      : [
          OnboardingAviationStep1,
          OnboardingAviationStep2,
          OnboardingAviationStep3,
        ]),
    OnboardingStep4,
    OnboardingCommitmentConfirmation,
  ];

  const mutationCallback = (profileData) => {
    profileData && setProfile(profileData);
  };

  const mutation = OperatorHooks.useSubmitOperatorProfile(mutationCallback);

  const StepComponent = steps[step];

  const goNext = (data) => {
    if (data?.commitment) setSelectedCommitment(data.commitment);
    if (step < steps.length - 1) {
      setPreviousStep(step);
      setStep(step + 1);
    } else {
      setOnboardingOpen(null);
    }
  };

  const goBack = () => {
    if (step > 0) {
      setPreviousStep(step);
      setStep(step - 1);
    }
  };

  const chooseCommitment = (data) => {
    mutation.mutateAsync(data);
  };

  const setOnboardingComplete = (data) => {
    trackActionActivity("ONBOARDING_COMPLETED", profile);
    mutation.mutateAsync(data);
  };

  const commitmentStepIdx = steps.length - 2;
  const confirmationStepIdx = steps.length - 1;

  const submitActions = {
    [commitmentStepIdx]: chooseCommitment,
    [confirmationStepIdx]: setOnboardingComplete,
  };

  return (
    <div className="min-h-full w-full">
      <div className="px-8 py-12 md:px-14 w-full min-h-full flex flex-col">
        <div className="grow">
          <StepComponent
            onNext={goNext}
            onBack={goBack}
            step={step}
            previous={previousStep}
            submitAction={submitActions[step] || null}
            commitment={selectedCommitment}
          />
        </div>
        {/* Progress bars */}
        {step >= 1 && step < steps.length - 1 && (
          <div className="flex justify-center items-center p-4 space-x-1">
            {steps.map((s, idx) => {
              if (idx === steps.length - 1) return null;
              const color =
                idx + 1 <= step
                  ? "bg-primary-500 animate-pulse animate-animated animate-infinite animate-slow"
                  : "bg-gray-300";
              const corners =
                idx === 0
                  ? "rounded-l-full"
                  : idx === steps.length - 2
                    ? "rounded-r-full"
                    : "";
              return (
                <div
                  key={idx}
                  className={`w-16 h-2.5 ${corners} ${color}`}
                ></div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const OnboardingContainerControls = {};
OnboardingContainer.propTypes = AppUtils.getPropTypeFromControls(
  OnboardingContainerControls,
);

export default OnboardingContainer;
